import numeral from 'numeral'

import { buildDynamicCopy } from '@mx-cartographer/experiences'
import { DATE_FORMATS_INTL, formatDate } from '@mx-cartographer/experiences'
import { Text } from '@mxenabled/mxui'

import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import { Card, CardContent, CardHeader, Divider, Stack } from '@mui/material'

import { useCopy } from '../../context/RulesEngineDataProvider'
import { CustomInsight } from '../../types/CustomInsight'

import { StatusLink } from '../shared/StatusLink'
import { StatusChip } from '../shared/StatusChip'

interface InsightCardProps {
  insight: CustomInsight
}

export const InsightCard: React.FC<InsightCardProps> = ({ insight }) => {
  const copy = useCopy()

  return (
    <Card sx={{ borderRadius: '8px', height: 165 }}>
      <CardHeader
        action={<StatusLink copy={copy} status={insight.status} />}
        title={
          <Text
            bold={true}
            variant="body1"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis', // Ensures that the title does not overflow because long titles were pushing the action out of the component at certain break points
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {insight.title}
          </Text>
        }
        sx={{
          // added styles to ensure that a long title does not push the action out of view
          '& .MuiCardHeader-content': {
            overflow: 'hidden',
            minWidth: 0,
            flex: '1 1 auto',
          },
          '& .MuiCardHeader-action': {
            flex: '0 0 auto',
            marginTop: 0,
            marginRight: 0,
          },
        }}
      />

      <Divider />

      <CardContent>
        <Stack gap={16}>
          <Stack alignItems="center" direction="row" gap={8} justifyContent="flex-start">
            <LightbulbOutlinedIcon sx={{ height: 16, width: 16 }} />
            <Text variant="body2">
              {insight.final_generated_count === 1
                ? copy.one_insight_generated
                : (buildDynamicCopy(
                    copy.num_insights_generated,
                    numeral(insight.final_generated_count).format('0a'), // Adds formating to prevent large numbers from overflowing
                  ) as string)}
            </Text>
            <StatusChip copy={copy} status={insight.status} />
          </Stack>
          <Stack alignItems="center" direction="row" gap={8} justifyContent="flex-start">
            <AccessTimeOutlinedIcon sx={{ height: 16, width: 16 }} />
            <Text variant="body2">
              {
                buildDynamicCopy(
                  copy.created_on_date,
                  formatDate(insight.created_at, DATE_FORMATS_INTL.YEAR_MONTH_DAY),
                ) as string
              }
            </Text>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}
